import { Box, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';

import { sanitizeElement } from '../../../utils/common';

interface PreviewDrawerNotesProps {
  logs?: Array<Record<string, unknown>>;
}

const MAX_VISIBLE_NOTES = 5;

const formatNoteDate = (value: unknown): string => {
  if (!value) return '';
  try {
    const d = new Date(value as string);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

interface NoteItem {
  date: string;
  author: string;
  text: string;
}

export const PreviewDrawerNotes: React.FC<PreviewDrawerNotesProps> = ({ logs }) => {
  const theme = useTheme();

  const notes: NoteItem[] = useMemo(() => {
    if (!logs || logs.length === 0) return [];

    return logs
      .filter((log) => {
        const notes = log.notes ?? log.note;
        return typeof notes === 'string' && notes.length > 0;
      })
      .slice(0, MAX_VISIBLE_NOTES)
      .map((log) => ({
        date: formatNoteDate(log.time ?? log.createdAt ?? log.createdDate),
        author: String(log.userName ?? log.createdBy ?? ''),
        text: String(log.notes ?? log.note ?? ''),
      }));
  }, [logs]);

  return (
    <Box>
      <Typography variant="overline" sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: 1.2, color: 'text.secondary', mb: 1, display: 'block' }}>
        RECENT NOTES
      </Typography>
      {notes.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
          No notes available
        </Typography>
      ) : (
        notes.map((note, idx) => (
          <Box key={idx} sx={{ py: 1, borderBottom: idx < notes.length - 1 ? `1px solid ${theme.palette.v4.border}` : 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                {note.author || 'System'}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                {note.date}
              </Typography>
            </Box>
            {/<[a-z][\s\S]*>/i.test(note.text) ? (
              <Box
                component="span"
                sx={{ fontSize: '0.8125rem', color: 'text.primary', '& p': { m: 0 } }}
                dangerouslySetInnerHTML={{ __html: sanitizeElement(note.text) }}
              />
            ) : (
              <Typography variant="body2" sx={{
                fontSize: '0.8125rem', color: 'text.primary',
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              }}>
                {note.text}
              </Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  );
};
